import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  ClipboardCheck,
  FileUp,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { PortalShortcutHint } from './PortalShortcutHint';
import { usePortalShortcutAria } from './portalShortcutContext';
import {
  VERTRAG_BERATUNG_CHECKLIST,
  type AktenChecklistItemDef,
} from './aktenTemplates';
import { checklistPointHidden } from './portalAkteChecklist';
import {
  addChecklistItemToAkte,
  buildPortalAkteNcPath,
  computeAktenGuardProgressFromChecklist,
  addPortalAkteMember,
  createPortalAkteVertragBeratung,
  findPortalAkteForPath,
  listKanzleiSeatsForAktePicker,
  ncPathForNewPortalAkte,
  loadPortalAktenChecklistExpanded,
  loadPortalAktenPanelExpanded,
  listChecklistItemsForAkte,
  listMyPortalAkteMemberAkteIds,
  listPortalAktenForCustomer,
  removeChecklistItemFromAkte,
  savePortalAktenChecklistExpanded,
  savePortalAktenPanelExpanded,
  updateChecklistItemStatus,
  updatePortalAkteNcPath,
} from './portalAkten';
import type {
  KanzleiSeatForAktePicker,
  PortalAkteRecord,
  PortalAktenChecklistRecord,
} from './portalAktenTypes';
import {
  AKTE_ELEMENT_ROOM_ENDPOINT,
  AKTE_WIZARD_STEPS,
  judgeAkteElementRoomResponse,
  planAkteElementRoom,
  suggestAkteElementRoomName,
  type AkteWizardStep,
} from './akteWizardElementRoom';
import { seatHasInvitableElementAccount } from './elementSeatInvite';
import { getPortalPocketBaseUrl } from '../../constants';
import {
  PORTAL_DOCUMENT_FILE_ACCEPT,
  PORTAL_IMAGE_OCR_NOTICE,
  portalImageUploadPolicyError,
  portalCustomerFacingRelativeFolder,
  portalDocumentRelativeFolder,
  sanitizePortalFolderInput,
} from './portalDocuments';
import type { PortalDocumentOpenFields, PortalProtokoll } from './portalDocuments';
import { PortalAkteMitgliederPanel } from './PortalAkteMitgliederPanel';
import {
  coercePortalAkteMemberAccess,
  filterPortalAktenForActor,
  isItAdminAkteOverview,
} from './portalCustomerAccess';
import type { PortalRole } from './portalCustomerAccess';

const MIN_RELOAD_INTERVAL_MS = 15_000;

function aktenzeichenLabel(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 'Akte';
  if (words.length === 1) return words[0].slice(0, 8);
  return words
    .slice(0, 3)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function buildChecklistTooltip(
  checklist: PortalAktenChecklistRecord[],
  progress: ReturnType<typeof computeAktenGuardProgressFromChecklist> | null,
): string {
  if (!checklist.length) return 'Keine Checklistenpunkte';
  const lines: string[] = [];
  if (progress) {
  const openRequired = progress.requiredTotal - progress.requiredDone;
    lines.push(
      progress.allRequiredDone
        ? 'Alle Pflichtpunkte erledigt'
        : `${openRequired}/${progress.requiredTotal} Pflicht offen`,
    );
  }
  for (const row of checklist) {
    const done = row.status === 'done' || row.status === 'waived';
    const mark = done ? '✓' : '○';
    const suffix = row.status === 'waived' ? ' (entfällt)' : row.required ? '' : ' (optional)';
    lines.push(`${mark} ${row.label}${suffix}`);
  }
  return lines.join('\n');
}

export interface PortalAktenwaechterPanelProps {
  customerId: string;
  kanzleiId?: string;
  authToken?: string | null;
  portalRole?: PortalRole;
  isPrimary?: boolean;
  /** NC-Ordner der Akte (Editor) oder relativer Dokumenten-Ordner (Hub). */
  aktePath?: string | null;
  /** Im Dokumenten-Hub: alle Akten anzeigen + Auswahl, wenn kein Pfad-Match. */
  showAktenPicker?: boolean;
  /** Dokumente für Drag & Drop-Verknüpfung (Dokumenten-Hub). */
  linkableDocuments?: PortalProtokoll[];
  className?: string;
  completedByEmail?: string;
  embedMode?: 'standalone' | 'strip' | 'hidden';
  onCollapsedSummaryChange?: (summary: string | null) => void;
  /** Dokumente-Hub: liefert die geladenen Akten für die Gruppierung der Dokumentliste. */
  onAktenChange?: (akten: PortalAkteRecord[]) => void;
  /** Wizard Schritt 3: nutzt den vorhandenen Upload-Vertrag des Dokumente-Hubs. */
  onUploadFirstDocument?: (
    file: File,
    targetFolder?: string,
  ) => Promise<PortalDocumentOpenFields>;
  /** Drei-Kachel-Hub: Leiste bleibt offen, Einklappen-Chrome entfällt. */
  forceExpanded?: boolean;
  /** Hub: geöffnete Akte — Wizard einklappen und Picker auf diese Akte setzen. */
  preferredAkteId?: string | null;
  /** Aktuell fokussierte Akte (Picker / Pfad-Match) an den Dokumente-Hub. */
  onFocusedAkteChange?: (akte: PortalAkteRecord | null) => void;
  /** Hub-DnD: dieselbe Zuordnung wie Aktenspeicher-Zeile (`updatePortalAkteNcPath` + Move). */
  onAssignDropped?: (e: React.DragEvent, akte: PortalAkteRecord) => void | Promise<void>;
  /** Hub: Akte-IDs, in denen der Sitz Mitglied ist (IT-Admin-Überblick vs. Kreis). */
  onMemberAkteIdsChange?: (ids: string[] | null) => void;
  /** Drei-Kachel-Header: Formular „Neue Akte“ öffnen (Tick hochzählen). */
  openCreateRequestId?: number;
  /** Fallliste hat schon einen Anlegen-Knopf. */
  hideCreateButton?: boolean;
  /** Ersetzt das Dropdown „Akte wählen“ auf der Fallliste. */
  onCaseListQueryChange?: (query: string) => void;
  /** Akt-Hub S1 rendert Beteiligte/Kreis im eigenen klaren Reiter. */
  showMembers?: boolean;
}

function buildAktenCollapsedSummary(
  akte: PortalAkteRecord | null,
  progress: ReturnType<typeof computeAktenGuardProgressFromChecklist> | null,
  loadedOnce: boolean,
  loading: boolean,
  showCreateForm: boolean,
  createStep: AkteWizardStep,
): string | null {
  if (!loadedOnce && loading) return null;
  if (showCreateForm) return `Neue Akte · Schritt ${createStep}/${AKTE_WIZARD_STEPS.length}`;
  if (!akte) {
    return loadedOnce ? 'Keine Akte — anlegen' : null;
  }
  if (!progress) return akte.title;
  const openRequired = progress.requiredTotal - progress.requiredDone;
  if (progress.allRequiredDone) return `${akte.title} · bereit`;
  return `${akte.title} · ${openRequired}/${progress.requiredTotal} Pflicht offen`;
}

export const PortalAktenwaechterPanel: React.FC<PortalAktenwaechterPanelProps> = ({
  customerId,
  kanzleiId,
  authToken,
  portalRole,
  isPrimary,
  aktePath,
  showAktenPicker = false,
  linkableDocuments = [],
  className = '',
  completedByEmail,
  embedMode = 'standalone',
  onCollapsedSummaryChange,
  onAktenChange,
  onUploadFirstDocument,
  forceExpanded = false,
  preferredAkteId = null,
  onFocusedAkteChange,
  onAssignDropped,
  onMemberAkteIdsChange,
  openCreateRequestId = 0,
  showMembers = true,
  hideCreateButton = false,
  onCaseListQueryChange,
}) => {
  const zurueckShortcut = usePortalShortcutAria('zurueck');
  const [akte, setAkte] = useState<PortalAkteRecord | null>(null);
  const [allAkten, setAllAkten] = useState<PortalAkteRecord[]>([]);
  const [memberAkteIds, setMemberAkteIds] = useState<Set<string> | null>(null);
  const [checklist, setChecklist] = useState<PortalAktenChecklistRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [busyItemId, setBusyItemId] = useState<string | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createStep, setCreateStep] = useState<AkteWizardStep>(1);
  const [wizardSeats, setWizardSeats] = useState<KanzleiSeatForAktePicker[]>([]);
  const [selectedSeatIds, setSelectedSeatIds] = useState<Set<string>>(() => new Set());
  const [elementRoomName, setElementRoomName] = useState('');
  const [elementRoomNameTouched, setElementRoomNameTouched] = useState(false);
  const [newAkteTitle, setNewAkteTitle] = useState('');
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(() =>
    new Set(VERTRAG_BERATUNG_CHECKLIST.items.map((i) => i.id)),
  );
  const [firstDocumentMode, setFirstDocumentMode] = useState<'skip' | 'upload' | 'existing'>('skip');
  const [firstDocumentFile, setFirstDocumentFile] = useState<File | null>(null);
  const [firstDocumentId, setFirstDocumentId] = useState('');
  const [newItemLabel, setNewItemLabel] = useState('');
  const [addItemBusy, setAddItemBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [dropActive, setDropActive] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [wizardDismissed, setWizardDismissed] = useState(false);
  const [standaloneExpanded, setStandaloneExpanded] = useState(() =>
    loadPortalAktenPanelExpanded(customerId),
  );
  const [checklistExpanded, setChecklistExpanded] = useState(() =>
    loadPortalAktenChecklistExpanded(customerId),
  );

  const loadedOnceRef = useRef(false);
  const lastReloadAtRef = useRef(0);
  const authTokenRef = useRef(authToken);
  const aktePathRef = useRef(aktePath);
  const allAktenRef = useRef(allAkten);
  const memberAkteIdsRef = useRef(memberAkteIds);
  const akteIdRef = useRef(akte?.id ?? null);
  const preferredAkteIdRef = useRef(preferredAkteId);
  const prevCustomerIdRef = useRef<string | null>(null);
  authTokenRef.current = authToken;
  aktePathRef.current = aktePath;
  allAktenRef.current = allAkten;
  memberAkteIdsRef.current = memberAkteIds;
  akteIdRef.current = akte?.id ?? null;
  preferredAkteIdRef.current = preferredAkteId;

  const templateItems = useMemo(
    () => [...VERTRAG_BERATUNG_CHECKLIST.items].sort((a, b) => a.order - b.order),
    [],
  );

  const assignableDocuments = useMemo(
    () =>
      [...linkableDocuments]
        .filter((doc) => Boolean(portalDocumentRelativeFolder(doc.nextcloud_path)))
        .sort(
          (a, b) =>
            new Date(b.updated || b.created).getTime() -
            new Date(a.updated || a.created).getTime(),
        ),
    [linkableDocuments],
  );

  const selectedExistingDocument = useMemo(
    () => assignableDocuments.find((doc) => doc.id === firstDocumentId) ?? null,
    [assignableDocuments, firstDocumentId],
  );

  const progress = useMemo(() => {
    if (!checklist.length) return null;
    return computeAktenGuardProgressFromChecklist(checklist);
  }, [checklist]);

  const checklistTooltip = useMemo(
    () => buildChecklistTooltip(checklist, progress),
    [checklist, progress],
  );

  const isTeamAdmin = portalRole === 'it_admin' || isPrimary === true;

  const visibleAkten = useMemo(
    () =>
      filterPortalAktenForActor(
        allAkten,
        { portal_role: portalRole, is_primary: isPrimary },
        memberAkteIds,
      ),
    [allAkten, isPrimary, memberAkteIds, portalRole],
  );

  const akteSelectLabel = useCallback(
    (row: PortalAkteRecord): string => {
      if (
        isItAdminAkteOverview(
          { id: customerId, portal_role: portalRole, is_primary: isPrimary },
          row,
          memberAkteIds,
        )
      ) {
        return `${row.title} · Überblick (IT-Admin)`;
      }
      return row.title;
    },
    [customerId, isPrimary, memberAkteIds, portalRole],
  );

  useEffect(() => {
    if (!loadedOnce) return;
    onAktenChange?.(visibleAkten);
  }, [visibleAkten, onAktenChange, loadedOnce]);

  useEffect(() => {
    onMemberAkteIdsChange?.(memberAkteIds ? Array.from(memberAkteIds) : null);
  }, [memberAkteIds, onMemberAkteIdsChange]);

  useEffect(() => {
    onFocusedAkteChange?.(akte);
  }, [akte, onFocusedAkteChange]);

  useEffect(() => {
    setStandaloneExpanded(loadPortalAktenPanelExpanded(customerId));
    setChecklistExpanded(loadPortalAktenChecklistExpanded(customerId));
  }, [customerId]);

  useEffect(() => {
    if (embedMode !== 'standalone') return;
    savePortalAktenPanelExpanded(customerId, standaloneExpanded);
  }, [customerId, embedMode, standaloneExpanded]);

  useEffect(() => {
    savePortalAktenChecklistExpanded(customerId, checklistExpanded);
  }, [customerId, checklistExpanded]);

  const reloadChecklist = useCallback(
    async (akteId: string, options?: { silent?: boolean }) => {
      const token = authTokenRef.current?.trim() ?? '';
      if (!token || !customerId) return;
      if (!options?.silent && !loadedOnceRef.current) setLoading(true);
      try {
        const rows = await listChecklistItemsForAkte(customerId, token, akteId);
        setChecklist(rows.filter((row) => !checklistPointHidden(row)));
      } finally {
        if (!options?.silent && !loadedOnceRef.current) setLoading(false);
      }
    },
    [customerId],
  );

  const reload = useCallback(
    async (options?: { silent?: boolean; force?: boolean; akteIdOverride?: string }) => {
      const token = authTokenRef.current?.trim() ?? '';
      if (!token || !customerId) return;

      const now = Date.now();
      if (!options?.force && now - lastReloadAtRef.current < MIN_RELOAD_INTERVAL_MS) return;
      lastReloadAtRef.current = now;

      const showSpinner = !options?.silent && !loadedOnceRef.current;
      if (showSpinner) setLoading(true);
      try {
        const aktenList = await listPortalAktenForCustomer(customerId, token, kanzleiId);
        setAllAkten(aktenList);
        let loadedMemberAkteIds: Set<string> | null;
        if (kanzleiId) {
          const mine = await listMyPortalAkteMemberAkteIds(token, customerId, kanzleiId);
          loadedMemberAkteIds = mine ? new Set(mine) : null;
        } else {
          loadedMemberAkteIds = new Set(aktenList.map((row) => row.id));
        }
        setMemberAkteIds(loadedMemberAkteIds);
        const scopedAkten = filterPortalAktenForActor(
          aktenList,
          { portal_role: portalRole, is_primary: isPrimary },
          loadedMemberAkteIds,
        );

        let resolved: PortalAkteRecord | null = null;
        if (options?.akteIdOverride) {
          resolved = scopedAkten.find((a) => a.id === options.akteIdOverride) ?? null;
        } else {
          try {
            const candidate = await findPortalAkteForPath(
              customerId,
              token,
              aktePathRef.current,
              kanzleiId,
            );
            resolved = candidate
              ? scopedAkten.find((row) => row.id === candidate.id) ?? null
              : null;
          } catch {
            resolved =
              scopedAkten.find((a) => a.id === preferredAkteIdRef.current) ??
              scopedAkten[0] ??
              null;
          }
        }

        setAkte(resolved);
        if (resolved) {
          await reloadChecklist(resolved.id, { silent: true });
        } else {
          setChecklist([]);
        }
        loadedOnceRef.current = true;
        setLoadedOnce(true);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [customerId, isPrimary, kanzleiId, portalRole, reloadChecklist],
  );

  useEffect(() => {
    const customerChanged = prevCustomerIdRef.current !== customerId;
    prevCustomerIdRef.current = customerId;

    if (customerChanged) {
      lastReloadAtRef.current = 0;
      loadedOnceRef.current = false;
      setLoadedOnce(false);
      void reload({ force: true });
      return;
    }

    if (preferredAkteIdRef.current?.trim()) return;

    lastReloadAtRef.current = 0;
    void reload({ force: true, silent: loadedOnceRef.current });
  }, [customerId, aktePath, kanzleiId, reload]);

  const openCreateForm = useCallback(() => {
    setNewAkteTitle('Neue Akte');
    setSelectedTemplateIds(new Set(VERTRAG_BERATUNG_CHECKLIST.items.map((i) => i.id)));
    setCreateStep(1);
    setFirstDocumentMode('skip');
    setFirstDocumentFile(null);
    setFirstDocumentId('');
    setSelectedSeatIds(new Set(customerId ? [customerId] : []));
    setElementRoomName('');
    setElementRoomNameTouched(false);
    setShowCreateForm(true);
    setStandaloneExpanded(true);
    setWizardDismissed(false);
    setMessage(null);
  }, [customerId]);

  useEffect(() => {
    if (!showAktenPicker || wizardDismissed || showCreateForm) return;
    if (loadedOnce && visibleAkten.length === 0 && portalRole !== 'praktikant') openCreateForm();
  }, [
    visibleAkten.length,
    loadedOnce,
    openCreateForm,
    portalRole,
    showAktenPicker,
    showCreateForm,
    wizardDismissed,
  ]);

  useEffect(() => {
    if (!openCreateRequestId) return;
    if (portalRole === 'praktikant') return;
    openCreateForm();
  }, [openCreateForm, openCreateRequestId, portalRole]);

  useEffect(() => {
    const id = preferredAkteId?.trim();
    if (!id) return;
    setShowCreateForm(false);
    setWizardDismissed(true);
    const scopedAkten = filterPortalAktenForActor(
      allAktenRef.current,
      { portal_role: portalRole, is_primary: isPrimary },
      memberAkteIdsRef.current,
    );
    const existing = scopedAkten.find((row) => row.id === id);
    if (akteIdRef.current === id && existing) return;
    if (existing) {
      setAkte(existing);
      void reloadChecklist(id, { silent: true });
      return;
    }
    void reload({ force: true, silent: true, akteIdOverride: id });
  }, [isPrimary, portalRole, preferredAkteId, reload, reloadChecklist]);

  const toggleTemplateItem = useCallback((itemId: string) => {
    setSelectedTemplateIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!showCreateForm || !kanzleiId || !authToken?.trim()) return;
    let cancelled = false;
    void listKanzleiSeatsForAktePicker(authToken.trim(), kanzleiId).then((rows) => {
      if (!cancelled) setWizardSeats(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [authToken, kanzleiId, showCreateForm]);

  const selectedWizardSeats = useMemo(() => {
    const byId = new Map(wizardSeats.map((seat) => [seat.id, seat]));
    const ids = new Set(selectedSeatIds);
    if (customerId) ids.add(customerId);
    return Array.from(ids).map((id) => {
      const known = byId.get(id);
      if (known) return known;
      return { id, email: '', name: '', element_seat: false };
    });
  }, [customerId, selectedSeatIds, wizardSeats]);

  const handleCreateAkte = useCallback(async (includeRoom: boolean) => {
    const token = authTokenRef.current?.trim() ?? '';
    if (!token) {
      setMessage('Bitte neu anmelden.');
      return;
    }
    const title = newAkteTitle.trim();
    if (!title) {
      setMessage('Bitte einen Akten-Titel eingeben.');
      return;
    }
    if (!selectedTemplateIds.size) {
      setMessage('Mindestens ein Checklisten-Punkt auswählen.');
      return;
    }
    if (firstDocumentMode === 'upload' && (!firstDocumentFile || !onUploadFirstDocument)) {
      setMessage('Bitte eine Datei für den Upload auswählen.');
      return;
    }
    if (firstDocumentMode === 'existing' && !selectedExistingDocument) {
      setMessage('Bitte ein vorhandenes Dokument mit Aktenordner auswählen.');
      return;
    }

    setCreateBusy(true);
    setMessage(null);
    try {
      const existingPath = selectedExistingDocument
        ? portalDocumentRelativeFolder(selectedExistingDocument.nextcloud_path)
        : '';
      const ncPath = ncPathForNewPortalAkte({
        mode: firstDocumentMode,
        existingDocumentFolder: existingPath,
        previousAktePath: aktePathRef.current,
      });
      const result = await createPortalAkteVertragBeratung(customerId, token, {
        title,
        nc_path: ncPath,
        kanzlei_id: kanzleiId,
        checklist_item_ids: Array.from(selectedTemplateIds),
      });
      if (result.ok === false) {
        setMessage(result.error);
        return;
      }
      setAkte(result.data.akte);
      onFocusedAkteChange?.(result.data.akte);
      setAllAkten((prev) => [result.data.akte, ...prev.filter((a) => a.id !== result.data.akte.id)]);
      setMemberAkteIds((prev) => {
        const next = new Set(prev ?? []);
        next.add(result.data.akte.id);
        return next;
      });
      await reloadChecklist(result.data.akte.id, { silent: true });
      lastReloadAtRef.current = 0;
      void reload({ force: true, akteIdOverride: result.data.akte.id });

      let followUp = '';
      if (firstDocumentMode === 'upload' && firstDocumentFile && onUploadFirstDocument) {
        try {
          const newFolder =
            portalDocumentRelativeFolder(result.data.akte.nc_path) ||
            (result.data.akte.kanzlei_id
              ? portalDocumentRelativeFolder(
                  buildPortalAkteNcPath(result.data.akte.kanzlei_id, result.data.akte.id),
                )
              : '');
          await onUploadFirstDocument(firstDocumentFile, newFolder || undefined);
          followUp = ' · Erstes Dokument hochgeladen.';
        } catch {
          followUp = ' · Akte ist angelegt; der Dokument-Upload ist fehlgeschlagen.';
        }
      } else if (firstDocumentMode === 'existing' && selectedExistingDocument) {
        followUp = ` · „${selectedExistingDocument.filename}" zugeordnet.`;
      }

      const akteId = result.data.akte.id;
      let memberNote = '';
      for (const seat of selectedWizardSeats) {
        if (!seat.id || seat.id === customerId) continue;
        const added = await addPortalAkteMember(token, {
          akte_id: akteId,
          kanzlei_id: kanzleiId,
          customer_id: seat.id,
          access: coercePortalAkteMemberAccess({
            access: 'write',
            targetRole: seat.portal_role,
            isAktenfuehrer: false,
          }),
        });
        if (added.ok === false) {
          memberNote = ' · Eine Person konnte nicht in den Kreis.';
          if (includeRoom && seatHasInvitableElementAccount(seat)) {
            setShowCreateForm(false);
            setCreateStep(1);
            setMessage(
              `Akte angelegt — ${result.data.checklistCount} Checklistenpunkte${followUp}${memberNote} Raum nicht angelegt.`,
            );
            return;
          }
        }
      }

      let roomNote = '';
      if (includeRoom) {
        const plan = planAkteElementRoom({
          includeRoom: true,
          name: elementRoomName || suggestAkteElementRoomName(title),
          akteId,
          seats: selectedWizardSeats.filter(
            (seat) => seat.id !== customerId && selectedSeatIds.has(seat.id),
          ),
          excludeSeatId: customerId,
        });
        if (!plan.post || plan.ok === false) {
          roomNote = ` · ${plan.post ? plan.message : 'Raum nicht angelegt.'}`;
        } else {
          const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
          try {
            const response = await fetch(`${pbBase}${AKTE_ELEMENT_ROOM_ENDPOINT}`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(plan.body),
            });
            const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
            const judged = judgeAkteElementRoomResponse(data, plan.body.akte_id);
            roomNote =
              response.ok && judged.ok
                ? ' · Element-Raum angelegt.'
                : ` · ${judged.ok ? 'Raum nicht angelegt.' : judged.message}`;
          } catch {
            roomNote = ' · Raum nicht angelegt.';
          }
        }
      }

      setShowCreateForm(false);
      setCreateStep(1);
      setFirstDocumentMode('skip');
      setFirstDocumentFile(null);
      setChecklistExpanded(false);
      savePortalAktenChecklistExpanded(customerId, false);
      setFirstDocumentId('');
      setMessage(
        `Akte angelegt — ${result.data.checklistCount} Checklistenpunkte${followUp}${memberNote}${roomNote}`,
      );
    } finally {
      setCreateBusy(false);
    }
  }, [
    customerId,
    elementRoomName,
    kanzleiId,
    firstDocumentFile,
    firstDocumentMode,
    newAkteTitle,
    onUploadFirstDocument,
    reload,
    reloadChecklist,
    onFocusedAkteChange,
    selectedExistingDocument,
    selectedSeatIds,
    selectedTemplateIds,
    selectedWizardSeats,
  ]);

  const linkDocumentToAkte = useCallback(
    async (doc: PortalProtokoll) => {
      if (!akte) return;
      const token = authTokenRef.current?.trim() ?? '';
      if (!token) return;
      const folder = portalDocumentRelativeFolder(doc.nextcloud_path);
      const linkPath = folder || aktePathRef.current?.trim() || '';
      if (!linkPath) {
        setMessage('Kein Ordnerpfad für die Verknüpfung verfügbar.');
        return;
      }
      setLinkBusy(true);
      setMessage(null);
      try {
        const result = await updatePortalAkteNcPath(token, akte.id, linkPath);
        if (result.ok === false) {
          setMessage(result.error);
          return;
        }
        setAkte(result.data);
        setMessage(`Akte mit Ordner „${linkPath}" verknüpft.`);
      } finally {
        setLinkBusy(false);
      }
    },
    [akte],
  );

  const handleAkteDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDropActive(false);

      if (!akte) {
        setMessage('Bitte zuerst eine Akte fokussieren — Zuordnung wird nicht geraten.');
        return;
      }

      if (onAssignDropped) {
        await onAssignDropped(e, akte);
        return;
      }

      const docId = e.dataTransfer.getData('text/plain')?.trim();
      const doc = docId ? linkableDocuments.find((d) => d.id === docId) : undefined;

      if (doc) {
        await linkDocumentToAkte(doc);
        return;
      }

      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length) {
        const folder = portalDocumentRelativeFolder(akte.nc_path) || sanitizePortalFolderInput(akte.nc_path ?? '');
        if (!folder) {
          setMessage('Die fokussierte Akte hat noch keinen Ordner — zuerst ein Dokument zuordnen.');
          return;
        }
        if (!onUploadFirstDocument) {
          setMessage('Datei hier abgelegt — bitte zuerst unter „Hinzufügen" hochladen; danach per Drag auf die Akte verknüpfen.');
          return;
        }
        setLinkBusy(true);
        setMessage(null);
        try {
          await onUploadFirstDocument(files[0], folder);
          setMessage(`Datei in die fokussierte Akte „${akte.title}" gelegt.`);
        } catch {
          setMessage('Upload in die fokussierte Akte ist fehlgeschlagen.');
        } finally {
          setLinkBusy(false);
        }
        return;
      }

      setMessage('Ziehen Sie ein Dokument aus der Liste auf die Akte, um den Ordner zu verknüpfen.');
    },
    [akte, linkDocumentToAkte, linkableDocuments, onAssignDropped, onUploadFirstDocument],
  );

  const handleToggleDone = useCallback(
    async (row: PortalAktenChecklistRecord) => {
      const token = authTokenRef.current?.trim() ?? '';
      if (!token) return;
      const nextStatus = row.status === 'done' ? 'open' : 'done';
      setBusyItemId(row.id);
      setMessage(null);
      try {
        const result = await updateChecklistItemStatus(token, row.id, nextStatus, completedByEmail);
        if (result.ok === false) {
          setMessage(result.error);
          return;
        }
        setChecklist((prev) => prev.map((item) => (item.id === row.id ? result.data : item)));
      } finally {
        setBusyItemId(null);
      }
    },
    [completedByEmail],
  );

  const handleWaive = useCallback(
    async (row: PortalAktenChecklistRecord) => {
      const token = authTokenRef.current?.trim() ?? '';
      if (!token) return;
      const nextStatus = row.status === 'waived' ? 'open' : 'waived';
      setBusyItemId(row.id);
      setMessage(null);
      try {
        const result = await updateChecklistItemStatus(token, row.id, nextStatus, completedByEmail);
        if (result.ok === false) {
          setMessage(result.error);
          return;
        }
        setChecklist((prev) => prev.map((item) => (item.id === row.id ? result.data : item)));
      } finally {
        setBusyItemId(null);
      }
    },
    [completedByEmail],
  );

  const handleAddItem = useCallback(async () => {
    if (!akte) return;
    const token = authTokenRef.current?.trim() ?? '';
    if (!token) return;
    const label = newItemLabel.trim();
    if (!label) return;

    setAddItemBusy(true);
    setMessage(null);
    try {
      const result = await addChecklistItemToAkte(customerId, token, akte.id, label, false);
      if (result.ok === false) {
        setMessage(result.error);
        return;
      }
      setChecklist((prev) => [...prev, result.data].sort((a, b) => a.sort_order - b.sort_order));
      setNewItemLabel('');
    } finally {
      setAddItemBusy(false);
    }
  }, [akte, customerId, newItemLabel]);

  const handleRemoveItem = useCallback(async (row: PortalAktenChecklistRecord) => {
    if (!row.item_id.startsWith('custom_')) return;
    if (!window.confirm(`Punkt „${row.label}" entfernen?`)) return;
    const token = authTokenRef.current?.trim() ?? '';
    if (!token) return;
    setBusyItemId(row.id);
    setMessage(null);
    try {
      const result = await removeChecklistItemFromAkte(token, row.id);
      if (result.ok === false) {
        setMessage(result.error);
        return;
      }
      setChecklist((prev) => prev.filter((item) => item.id !== row.id));
    } finally {
      setBusyItemId(null);
    }
  }, []);

  const renderTemplateCheckbox = (item: AktenChecklistItemDef) => (
    <label
      key={item.id}
      className="flex items-start gap-2 rounded-md px-2 py-1 hover:bg-white/[0.03] cursor-pointer"
    >
      <input
        type="checkbox"
        checked={selectedTemplateIds.has(item.id)}
        onChange={() => toggleTemplateItem(item.id)}
        className="mt-0.5 rounded border-white/20"
      />
      <span className="min-w-0 flex-1">
        <span className="text-[11px] text-slate-200">{item.label}</span>
        <span className="block text-[10px] text-slate-600">{item.required ? 'Pflicht' : 'Optional'}</span>
      </span>
    </label>
  );

  const collapsedSummary = buildAktenCollapsedSummary(
    akte,
    progress,
    loadedOnce,
    loading,
    showCreateForm,
    createStep,
  );

  useEffect(() => {
    onCollapsedSummaryChange?.(collapsedSummary);
  }, [collapsedSummary, onCollapsedSummaryChange]);

  if (!authToken?.trim()) return null;

  if (embedMode === 'hidden') return null;

  const showCreateCta =
    !akte && loadedOnce && !showCreateForm && !showAktenPicker && Boolean(aktePath?.trim());
  const showHeaderCreateButton =
    !hideCreateButton &&
    showAktenPicker &&
    loadedOnce &&
    !showCreateForm &&
    portalRole !== 'praktikant';
  const caseListSearch = typeof onCaseListQueryChange === 'function';
  const showEmptyPathHint = !aktePath?.trim() && !showAktenPicker && !akte && loadedOnce;

  if (showEmptyPathHint) return null;

  const openRequired = progress ? progress.requiredTotal - progress.requiredDone : 0;

  const shellClass =
    embedMode === 'strip'
      ? `overflow-hidden ${className}`
      : caseListSearch
        ? `overflow-hidden border-0 bg-transparent ${className}`
        : `rounded-lg border border-white/[0.08] bg-white/[0.02] overflow-hidden ${className}`;
  const contentExpanded = forceExpanded || embedMode !== 'standalone' || standaloneExpanded;

  return (
    <div
      className={shellClass}
      aria-label="Aktenwächter"
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const types = Array.from(e.dataTransfer.types || []);
        const internal =
          types.includes('application/x-arkorbit-portal-doc') ||
          (types.includes('text/plain') && !types.includes('Files'));
        e.dataTransfer.dropEffect = internal ? 'move' : 'copy';
        setDropActive(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropActive(false);
      }}
      onDrop={(e) => void handleAkteDrop(e)}
    >
      {caseListSearch ? null : embedMode !== 'strip' ? (
        <div
          className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 ${
            contentExpanded ? 'border-b border-white/[0.06]' : ''
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <ClipboardCheck className={`w-3.5 h-3.5 flex-shrink-0 ${caseListSearch ? 'text-slate-500' : 'text-violet-400'}`} aria-hidden />
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 flex-shrink-0">
              Akte
            </span>
            <span className="text-[11px] text-slate-300 truncate">
              {collapsedSummary || 'Lade…'}
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {!showCreateForm && akte && progress ? (
              <span
                title={checklistTooltip}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border cursor-help ${
                  progress.allRequiredDone
                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                    : 'bg-amber-500/10 text-amber-200 border-amber-500/30'
                }`}
              >
                {aktenzeichenLabel(akte.title)}
                <span className="mx-1 opacity-50">·</span>
                {progress.allRequiredDone
                  ? 'bereit'
                  : `${openRequired}/${progress.requiredTotal} Pflicht offen`}
              </span>
            ) : null}
            {showHeaderCreateButton ? (
              <button
                type="button"
                onClick={openCreateForm}
                disabled={createBusy || linkBusy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-[11px] font-semibold px-3 py-1.5 shadow-sm"
              >
                <Plus className="w-3.5 h-3.5" />
                Neue Akte
              </button>
            ) : null}
            {showAktenPicker && visibleAkten.length > 0 ? (
              <select
                value={akte?.id ?? ''}
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) {
                    setAkte(null);
                    setChecklist([]);
                    return;
                  }
                  lastReloadAtRef.current = 0;
                  void reload({ force: true, akteIdOverride: id });
                }}
                className="max-w-[10rem] rounded border border-white/[0.08] bg-slate-900/80 px-1.5 py-0.5 text-[10px] text-slate-300"
                aria-label="Akte wählen"
              >
                <option value="">Akte wählen…</option>
                {visibleAkten.map((a) => (
                  <option key={a.id} value={a.id}>
                    {akteSelectLabel(a)}
                  </option>
                ))}
              </select>
            ) : null}
            <button
              type="button"
              onClick={() => {
                lastReloadAtRef.current = 0;
                void reload({ force: true, akteIdOverride: akte?.id });
              }}
              disabled={loading}
              className="p-0.5 text-slate-500 hover:text-slate-300 disabled:opacity-50"
              title="Aktenwächter neu laden"
              aria-label="Aktenwächter neu laden"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </button>
            {!forceExpanded ? (
            <button
              type="button"
              onClick={() => setStandaloneExpanded((value) => !value)}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] text-slate-400 hover:bg-white/[0.04] hover:text-slate-200"
              title={standaloneExpanded ? 'Aktenleiste einklappen' : 'Aktenleiste öffnen'}
              aria-label={standaloneExpanded ? 'Aktenleiste einklappen' : 'Aktenleiste öffnen'}
              aria-expanded={standaloneExpanded}
            >
              {standaloneExpanded ? (
                <ChevronUp className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
              {standaloneExpanded ? 'Einklappen' : 'Öffnen'}
            </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-1 px-3 py-1 border-b border-white/[0.06]">
          {!showCreateForm && akte && progress ? (
            <span
              title={checklistTooltip}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border cursor-help ${
                progress.allRequiredDone
                  ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                  : 'bg-amber-500/10 text-amber-200 border-amber-500/30'
              }`}
            >
              {aktenzeichenLabel(akte.title)}
              <span className="mx-1 opacity-50">·</span>
              {progress.allRequiredDone
                ? 'bereit'
                : `${openRequired}/${progress.requiredTotal} Pflicht offen`}
            </span>
          ) : null}
          {showAktenPicker && visibleAkten.length > 0 ? (
            <select
              value={akte?.id ?? ''}
              onChange={(e) => {
                const id = e.target.value;
                if (!id) {
                  setAkte(null);
                  setChecklist([]);
                  return;
                }
                lastReloadAtRef.current = 0;
                void reload({ force: true, akteIdOverride: id });
              }}
              className="max-w-[10rem] rounded border border-white/[0.08] bg-slate-900/80 px-1.5 py-0.5 text-[10px] text-slate-300"
              aria-label="Akte wählen"
            >
              <option value="">Akte wählen…</option>
              {visibleAkten.map((a) => (
                <option key={a.id} value={a.id}>
                  {akteSelectLabel(a)}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            onClick={() => {
              lastReloadAtRef.current = 0;
              void reload({ force: true, akteIdOverride: akte?.id });
            }}
            disabled={loading}
            className="p-0.5 text-slate-500 hover:text-slate-300 disabled:opacity-50"
            title="Aktenwächter neu laden"
            aria-label="Aktenwächter neu laden"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </button>
        </div>
      )}

      {contentExpanded && !(caseListSearch && !showCreateForm) ? (
        <>
      {!showCreateForm && progress ? (
        <div
          className={`px-3 py-2 text-xs border-b border-white/[0.06] ${
            progress.allRequiredDone
              ? 'bg-emerald-500/10 text-emerald-300'
              : 'bg-amber-500/5 text-amber-200/90'
          }`}
        >
          <div className="flex items-center gap-2">
            {progress.allRequiredDone ? (
              <ShieldCheck className="w-4 h-4 flex-shrink-0" aria-hidden />
            ) : (
              <Circle className="w-4 h-4 flex-shrink-0" aria-hidden />
            )}
            <span>
              {progress.allRequiredDone
                ? 'Akte bereit — alle gewählten Pflichtpunkte erledigt'
                : `${progress.requiredDone}/${progress.requiredTotal} Pflicht erledigt`}
              {progress.optionalTotal > 0
                ? ` · ${progress.optionalDone}/${progress.optionalTotal} optional`
                : ''}
            </span>
          </div>
        </div>
      ) : null}

      <div
        className={`px-3 py-2 space-y-2 ${dropActive ? 'bg-violet-500/10 ring-1 ring-violet-400/40 ring-inset' : ''}`}
      >
        {!showCreateForm && akte && dropActive ? (
          <p className="text-[10px] text-violet-300 m-0">Dokument oder Ordner auf die Akte ablegen…</p>
        ) : !showCreateForm && akte ? (
          <p className="text-[10px] text-slate-600 m-0">
            Tipp: Dokument aus der Liste auf die Akte ziehen, um den Ordner zu verknüpfen.
          </p>
        ) : null}

        {!showCreateForm && akte ? (
          <p className="text-[11px] text-slate-400 m-0">
            <span className="text-slate-200 font-medium">{akte.title}</span>
            {isItAdminAkteOverview(
              { id: customerId, portal_role: portalRole, is_primary: isPrimary },
              akte,
              memberAkteIds,
            )
              ? ' · Überblick (IT-Admin, nicht im Kreis)'
              : ''}
            {akte.mandant_label ? ` · ${akte.mandant_label}` : ''}
          </p>
        ) : null}

        {showAktenPicker && !caseListSearch ? (
          <p className="text-[10px] text-slate-500 m-0">
            {isTeamAdmin
              ? 'Sichtbar: Akten mit Mitgliedschaft, plus Überblick aller Kanzlei-Akten (IT-Admin).'
              : 'Sichtbar: nur Akten, in denen Sie Mitglied sind. Ordner ohne Akte bleiben kanzleiweit.'}
          </p>
        ) : null}

        {showMembers && akte && kanzleiId && authToken?.trim() ? (
          <PortalAkteMitgliederPanel
            akte={akte}
            authToken={authToken.trim()}
            actorId={customerId}
            kanzleiId={kanzleiId}
            portalRole={portalRole}
            isPrimary={isPrimary}
          />
        ) : null}

        {loading && !loadedOnce ? (
          <p className="text-[11px] text-slate-500 m-0 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Lade Checkliste…
          </p>
        ) : null}

        {showCreateCta ? (
          <div className="rounded-lg border border-dashed border-violet-500/30 bg-violet-500/5 px-3 py-3 space-y-2">
            <p className="text-[11px] text-slate-400 m-0">
              Keine Akte für diesen Ordner. Wählen Sie die Checklisten-Punkte für diese Akte.
            </p>
            <button
              type="button"
              onClick={openCreateForm}
              className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-[11px] font-semibold px-3 py-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              Neue Akte anlegen
            </button>
          </div>
        ) : null}

        {showCreateForm ? (
          <section
            className="rounded-lg border border-violet-500/25 bg-violet-500/5 px-3 py-3 space-y-3"
            aria-label="Neue Akte erstellen"
            data-portal-shortcut-scope="zurueck"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-slate-200 m-0 font-semibold">Neue Akte</p>
              <span className="text-[10px] text-slate-500">
                Schritt {createStep} von {AKTE_WIZARD_STEPS.length}
              </span>
            </div>
            <ol className="m-0 p-0 list-none grid grid-cols-2 gap-1.5 sm:grid-cols-5" aria-label="Fortschritt">
              {AKTE_WIZARD_STEPS.map((item) => (
                <li
                  key={item.step}
                  aria-current={createStep === item.step ? 'step' : undefined}
                  className={`rounded-md border px-2 py-1 text-center text-[10px] font-medium ${
                    createStep === item.step
                      ? 'border-violet-400/50 bg-violet-500/15 text-violet-200'
                      : item.step < createStep
                        ? 'border-emerald-500/25 bg-emerald-500/5 text-emerald-300'
                        : 'border-white/[0.06] text-slate-600'
                  }`}
                >
                  {item.step} · {item.label}
                </li>
              ))}
            </ol>

            {createStep === 1 ? (
              <div className="space-y-3">
                <label className="block space-y-1">
                  <span className="text-[11px] font-medium text-slate-300">Akten-Titel</span>
                  <input
                    type="text"
                    value={newAkteTitle}
                    onChange={(e) => setNewAkteTitle(e.target.value)}
                    placeholder="z. B. Mietrecht 2026"
                    autoFocus
                    className="w-full rounded border border-white/[0.08] bg-slate-900/70 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600"
                  />
                </label>
                <p className="text-[10px] text-slate-500 m-0">
                  Kanzlei und angemeldeter Sitz kommen aus der Anmeldung. Es gibt hier keine freie Sitz-Auswahl.
                  {aktePath?.trim() ? ` Ablage: ${aktePath.trim()}.` : ''}
                </p>
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    data-portal-shortcut="zurueck"
                    aria-keyshortcuts={zurueckShortcut}
                    title={zurueckShortcut}
                    onClick={() => {
                      setShowCreateForm(false);
                      setWizardDismissed(true);
                    }}
                    className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-300 px-3 py-2"
                  >
                    Abbrechen
                    <PortalShortcutHint id="zurueck" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!newAkteTitle.trim()) {
                        setMessage('Bitte einen Akten-Titel eingeben.');
                        return;
                      }
                      setMessage(null);
                      setCreateStep(2);
                    }}
                    className="rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-xs font-semibold px-4 py-2"
                    disabled={!newAkteTitle.trim()}
                  >
                    Weiter
                  </button>
                </div>
              </div>
            ) : null}

            {createStep === 2 ? (
              <div className="space-y-3">
                <div>
                  <p className="text-[11px] text-slate-300 m-0 font-medium">Standard-Checkliste</p>
                  <p className="text-[10px] text-slate-500 m-0 mt-0.5">
                    Sinnvolle Vertrags-/Beratungs-Defaults sind bereits gewählt. Details bleiben später änderbar.
                  </p>
                </div>
                <div className="max-h-56 overflow-y-auto space-y-0.5 border border-white/[0.06] rounded-md p-1">
                  {templateItems.map(renderTemplateCheckbox)}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[10px] text-slate-600 m-0">
                    {selectedTemplateIds.size} von {templateItems.length} Punkten gewählt
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedTemplateIds(
                        new Set(VERTRAG_BERATUNG_CHECKLIST.items.map((item) => item.id)),
                      )
                    }
                    className="text-[10px] text-violet-400 hover:text-violet-300"
                  >
                    Standards wiederherstellen
                  </button>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setCreateStep(1)}
                    className="text-[11px] text-slate-500 hover:text-slate-300 px-3 py-2"
                  >
                    Zurück
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreateStep(3)}
                    disabled={!selectedTemplateIds.size}
                    className="rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-xs font-semibold px-4 py-2"
                  >
                    Weiter
                  </button>
                </div>
              </div>
            ) : null}

            {createStep === 3 ? (
              <div className="space-y-3">
                <div>
                  <p className="text-[11px] text-slate-300 m-0 font-medium">Erstes Dokument (optional)</p>
                  <p className="text-[10px] text-slate-500 m-0 mt-0.5">
                    Hochladen, vorhandene Datei zuordnen oder diesen Schritt überspringen.
                  </p>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                  <button
                    type="button"
                    onClick={() => setFirstDocumentMode('skip')}
                    className={`rounded-lg border px-3 py-2 text-left ${
                      firstDocumentMode === 'skip'
                        ? 'border-violet-400/50 bg-violet-500/15'
                        : 'border-white/[0.08] hover:bg-white/[0.03]'
                    }`}
                  >
                    <span className="block text-[11px] font-medium text-slate-200">Überspringen</span>
                    <span className="block text-[10px] text-slate-600">Akte ohne erstes Dokument anlegen</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFirstDocumentMode('upload')}
                    disabled={!onUploadFirstDocument}
                    className={`rounded-lg border px-3 py-2 text-left disabled:opacity-40 ${
                      firstDocumentMode === 'upload'
                        ? 'border-emerald-400/50 bg-emerald-500/10'
                        : 'border-white/[0.08] hover:bg-white/[0.03]'
                    }`}
                  >
                    <FileUp className="w-4 h-4 text-emerald-400 mb-1" />
                    <span className="block text-[11px] font-medium text-slate-200">Hochladen</span>
                    <span className="block text-[10px] text-slate-600">Vorhandenen Upload nutzen</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFirstDocumentMode('existing')}
                    disabled={!assignableDocuments.length}
                    className={`rounded-lg border px-3 py-2 text-left disabled:opacity-40 ${
                      firstDocumentMode === 'existing'
                        ? 'border-sky-400/50 bg-sky-500/10'
                        : 'border-white/[0.08] hover:bg-white/[0.03]'
                    }`}
                  >
                    <Link2 className="w-4 h-4 text-sky-400 mb-1" />
                    <span className="block text-[11px] font-medium text-slate-200">Vorhandene Datei</span>
                    <span className="block text-[10px] text-slate-600">Über ihren Aktenordner zuordnen</span>
                  </button>
                </div>

                {firstDocumentMode === 'upload' ? (
                  <label className="block rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 space-y-1">
                    <span className="text-[11px] font-medium text-slate-300">Datei auswählen</span>
                    <input
                      type="file"
                      accept={PORTAL_DOCUMENT_FILE_ACCEPT}
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        const policyError = file
                          ? portalImageUploadPolicyError({ name: file.name, mimeType: file.type })
                          : null;
                        if (policyError) {
                          setFirstDocumentFile(null);
                          setMessage(policyError);
                          e.target.value = '';
                          return;
                        }
                        setFirstDocumentFile(file);
                      }}
                      className="block w-full text-[11px] text-slate-400 file:mr-3 file:rounded-md file:border-0 file:bg-emerald-600 file:px-3 file:py-1.5 file:text-[11px] file:font-semibold file:text-white hover:file:bg-emerald-500"
                    />
                    <span className="block text-[10px] text-slate-600">
                      Landet im neuen Aktenordner — nicht in der vorherigen oder der Testakte.
                    </span>
                    <span className="block text-[10px] text-amber-300/70">
                      {PORTAL_IMAGE_OCR_NOTICE}
                    </span>
                  </label>
                ) : null}

                {firstDocumentMode === 'existing' ? (
                  <label className="block space-y-1">
                    <span className="text-[11px] font-medium text-slate-300">Dokument auswählen</span>
                    <select
                      value={firstDocumentId}
                      onChange={(e) => setFirstDocumentId(e.target.value)}
                      className="w-full rounded border border-white/[0.08] bg-slate-900/90 px-3 py-2 text-[11px] text-slate-200"
                    >
                      <option value="">Vorhandenes Dokument wählen…</option>
                      {assignableDocuments.map((doc) => (
                        <option key={doc.id} value={doc.id}>
                          {doc.filename}
                          {portalCustomerFacingRelativeFolder(doc.nextcloud_path)
                            ? ` · ${portalCustomerFacingRelativeFolder(doc.nextcloud_path)}`
                            : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}

                <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                  <button
                    type="button"
                    data-portal-shortcut="zurueck"
                    aria-keyshortcuts={zurueckShortcut}
                    title={zurueckShortcut}
                    onClick={() => {
                      setShowCreateForm(false);
                      setWizardDismissed(true);
                    }}
                    disabled={createBusy}
                    className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-300 px-2 py-2"
                  >
                    Abbrechen
                    <PortalShortcutHint id="zurueck" />
                  </button>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setCreateStep(2)}
                      disabled={createBusy}
                      className="text-[11px] text-slate-500 hover:text-slate-300 px-3 py-2"
                    >
                      Zurück
                    </button>
                    <button
                      type="button"
                      onClick={() => setCreateStep(4)}
                      disabled={
                        createBusy ||
                        (firstDocumentMode === 'upload' && !firstDocumentFile) ||
                        (firstDocumentMode === 'existing' && !selectedExistingDocument)
                      }
                      className="rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-xs font-semibold px-4 py-2"
                    >
                      Weiter
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {createStep === 4 ? (
              <div className="space-y-3">
                <div>
                  <p className="text-[11px] text-slate-300 m-0 font-medium">Personen</p>
                  <p className="text-[10px] text-slate-500 m-0 mt-0.5">
                    Wer in diese Akte gehört. Element bleibt so, wie es am Sitz steht.
                  </p>
                </div>
                <ul className="m-0 p-0 list-none max-h-56 overflow-y-auto space-y-1 border border-white/[0.06] rounded-md p-2">
                  {selectedWizardSeats.length === 0 && wizardSeats.length === 0 ? (
                    <li className="text-[11px] text-slate-500">Keine Sitze geladen.</li>
                  ) : (
                    (wizardSeats.length ? wizardSeats : selectedWizardSeats).map((seat) => {
                      const checked = seat.id === customerId || selectedSeatIds.has(seat.id);
                      const label = seat.name || seat.email || 'Angemeldeter Sitz';
                      return (
                        <li key={seat.id}>
                          <label className="flex items-center gap-2 text-[11px] text-slate-200">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={seat.id === customerId}
                              onChange={() => {
                                if (seat.id === customerId) return;
                                setSelectedSeatIds((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(seat.id)) next.delete(seat.id);
                                  else next.add(seat.id);
                                  if (customerId) next.add(customerId);
                                  return next;
                                });
                              }}
                            />
                            <span className="truncate">{label}</span>
                            <span className="text-[10px] text-slate-500">
                              {seat.element_seat === true ? 'Element an' : 'Element aus'}
                            </span>
                          </label>
                        </li>
                      );
                    })
                  )}
                </ul>
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setCreateStep(3)}
                    className="text-[11px] text-slate-500 hover:text-slate-300 px-3 py-2"
                  >
                    Zurück
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!elementRoomNameTouched) {
                        setElementRoomName(suggestAkteElementRoomName(newAkteTitle));
                      }
                      setCreateStep(5);
                    }}
                    className="rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold px-4 py-2"
                  >
                    Weiter
                  </button>
                </div>
              </div>
            ) : null}

            {createStep === 5 ? (
              <div className="space-y-3">
                <div>
                  <p className="text-[11px] text-slate-300 m-0 font-medium">Element-Raum anlegen</p>
                  <p className="text-[10px] text-slate-500 m-0 mt-0.5">
                    Optional. Ohne diesen Schritt bleibt es bei der Akte. Nachjoinen später im Kanzlei-Chat.
                  </p>
                </div>
                <label className="block space-y-1">
                  <span className="text-[11px] font-medium text-slate-300">Raumname</span>
                  <input
                    type="text"
                    value={elementRoomName}
                    onChange={(e) => {
                      setElementRoomNameTouched(true);
                      setElementRoomName(e.target.value);
                    }}
                    className="w-full rounded border border-white/[0.08] bg-slate-900/70 px-3 py-2 text-sm text-slate-200"
                  />
                </label>
                <p className="text-[10px] text-slate-500 m-0">
                  Eingeladen werden nur angehakte Sitze mit Element-Konto. Der eigene Sitz geht nicht extra mit.
                  {selectedWizardSeats.some(
                    (seat) =>
                      seat.id !== customerId &&
                      selectedSeatIds.has(seat.id) &&
                      seatHasInvitableElementAccount(seat),
                  )
                    ? ` ${selectedWizardSeats
                        .filter(
                          (seat) =>
                            seat.id !== customerId &&
                            selectedSeatIds.has(seat.id) &&
                            seatHasInvitableElementAccount(seat),
                        )
                        .map((seat) => seat.name || seat.email || 'Sitz')
                        .join(', ')}.`
                    : ' Ohne Häkchen bleibt die Sitzliste leer.'}
                </p>
                <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setCreateStep(4)}
                    disabled={createBusy}
                    className="text-[11px] text-slate-500 hover:text-slate-300 px-2 py-2"
                  >
                    Zurück
                  </button>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void handleCreateAkte(false)}
                      disabled={createBusy}
                      className="rounded-lg border border-white/[0.1] px-3 py-2 text-[11px] text-slate-200 hover:bg-white/[0.04] disabled:opacity-50"
                    >
                      Ohne Raum fertig
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleCreateAkte(true)}
                      disabled={createBusy}
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-xs font-semibold px-4 py-2"
                    >
                      {createBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Element-Raum anlegen
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        {!showCreateForm && akte ? (
          <details
            className="rounded-lg border border-white/[0.08] bg-white/[0.02]"
            open={checklistExpanded}
            onToggle={(e) => {
              const next = (e.currentTarget as HTMLDetailsElement).open;
              setChecklistExpanded(next);
            }}
          >
            <summary className="cursor-pointer list-none px-3 py-2 flex items-center justify-between gap-2 text-[11px] text-slate-300 hover:text-white">
              <span className="font-semibold">
                Prüfliste
                {progress
                  ? ` · ${progress.allRequiredDone ? 'bereit' : `${openRequired}/${progress.requiredTotal} Pflicht offen`}`
                  : ''}
              </span>
              <span className="text-[10px] text-slate-500">
                {checklistExpanded ? 'Einklappen' : 'Aufklappen'}
              </span>
            </summary>
            <div className="border-t border-white/[0.06] px-2 py-2 space-y-2">
        {!showCreateForm && akte && checklist.length === 0 && loadedOnce && !loading ? (
          <p className="text-[11px] text-slate-500 m-0">Keine Checklistenpunkte — bitte aktualisieren.</p>
        ) : null}
        {!showCreateForm && akte && checklist.length > 0 ? (
          <ul className="m-0 p-0 list-none space-y-1">
            {checklist.map((row) => {
              const isDone = row.status === 'done';
              const isWaived = row.status === 'waived';
              const busy = busyItemId === row.id;
              const isCustom = row.item_id.startsWith('custom_');

              return (
                <li
                  key={row.id}
                  className={`flex items-start gap-2 rounded-md px-2 py-1.5 text-[11px] ${
                    isWaived ? 'opacity-60' : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => void handleToggleDone(row)}
                    disabled={busy || isWaived}
                    className="mt-0.5 flex-shrink-0 text-slate-400 hover:text-emerald-400 disabled:opacity-40"
                    title={isDone ? 'Als offen markieren' : 'Erledigt'}
                    aria-label={isDone ? 'Als offen markieren' : 'Erledigt'}
                  >
                    {busy ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isDone ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <Circle className="w-4 h-4" />
                    )}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p
                      className={`m-0 text-slate-200 ${
                        isDone || isWaived ? 'line-through text-slate-500' : ''
                      }`}
                    >
                      {row.label}
                    </p>
                    <p className="m-0 mt-0.5 text-[10px] text-slate-600">
                      {row.required ? 'Pflicht' : 'Optional'}
                      {isWaived ? ' · entfällt' : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => void handleWaive(row)}
                      disabled={busy}
                      className={`rounded px-1.5 py-0.5 text-[10px] border transition-colors ${
                        isWaived
                          ? 'border-slate-600 text-slate-400'
                          : 'border-white/[0.08] text-slate-500 hover:text-amber-300 hover:border-amber-500/40'
                      }`}
                      title={isWaived ? 'Entfällt zurücknehmen' : 'Entfällt'}
                    >
                      {isWaived ? '↩' : 'entfällt'}
                    </button>
                    {isCustom ? (
                      <button
                        type="button"
                        onClick={() => void handleRemoveItem(row)}
                        disabled={busy}
                        className="p-0.5 text-slate-600 hover:text-red-400 disabled:opacity-40"
                        title="Punkt entfernen"
                        aria-label="Punkt entfernen"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}

        {!showCreateForm && akte ? (
          <div className="flex flex-col sm:flex-row gap-1.5 pt-1">
            <input
              type="text"
              value={newItemLabel}
              onChange={(e) => setNewItemLabel(e.target.value)}
              placeholder="Eigener Punkt…"
              className="flex-1 min-w-0 rounded border border-white/[0.08] bg-slate-900/70 px-2 py-1 text-[11px] text-slate-200 placeholder:text-slate-600"
            />
            <button
              type="button"
              onClick={() => void handleAddItem()}
              disabled={addItemBusy || !newItemLabel.trim()}
              className="inline-flex items-center justify-center gap-1 rounded border border-white/[0.08] px-2 py-1 text-[10px] text-slate-300 hover:bg-white/[0.04] disabled:opacity-40"
            >
              {addItemBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Punkt
            </button>
          </div>
        ) : null}
            </div>
          </details>
        ) : null}

        {message ? <p className="text-[10px] text-amber-300 m-0">{message}</p> : null}
      </div>
        </>
      ) : null}
    </div>
  );
};
